// ============================================================================
// Safe Care - Suivi des souscriptions du client (contrats + demandes)
// Module : features/assistance_sociale/pages
// Charte : .tab / .formTitle / .bG / .bt_add / .tabItem / .tabElement / InputForm / SearchInput
// Pattern : identique à ProduitAdmin.tsx (formulaire dans Tab + tableau en dessous)
// ============================================================================

import { useState, useEffect } from 'react';
import { Table, Tabs, Spin, Button, Form, Space } from 'antd';
import type { TabsProps } from 'antd';
import { Box, Typography } from '@mui/material';
import { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import SweetAlert from 'sweetalert2';
import InputForm from '../../../Components/InputT';
import SearchModal from '../../../Components/SearchModal';
import Params from '../../../Types/ParamsPicklist';
import {
  useGetSouscriptionsByClient,
  useGetAllDemandesSouscription,
} from '../hooks';
import { SouscriptionSafeCare, DemandeSouscription } from '../types';

// Picklist client (table CLIENT existante) - l'agent sélectionne le client
const searchClient: Params = {
  CodeAgence: '',
  Table: 'CLIENT',
  Colonne1: 'NUMCLI',
  Colonne2: 'NOMCLI',
  Colagence: '101',
  Choix: 22,
};

const MesSouscriptions = ({ embedded = false }: { embedded?: boolean }) => {
  const navigate = useNavigate();
  const [numcliSelected, setNumcliSelected] = useState<string>('');

  const { data: souscriptionsRes, isLoading: loadingSous } = useGetSouscriptionsByClient(numcliSelected);
  const { data: demandes, isLoading: loadingDem } = useGetAllDemandesSouscription();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [souscriptionsData, setSouscriptionsData] = useState<SouscriptionSafeCare[]>([]);
  const [demandesData, setDemandesData] = useState<DemandeSouscription[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedSouscription, setSelectedSouscription] = useState<SouscriptionSafeCare | null>(null);
  const [activeTab, setActiveTab] = useState('1');

  // Etats des boutons d'action (charte cSAL)
  const [ajoutActve, setAjoutActif] = useState(false);
  const [modifActve, setModifActve] = useState(true);
  const [enregActve, setEnregActve] = useState(true);
  const [supActive, setSupActive] = useState(true);
  const [impActive, setImpActive] = useState(true);
  const [actuActive, setActActive] = useState(true);
  const [annulActive, setAnnulActive] = useState(true);
  const [consulActive, setConsulActive] = useState(false);

  useEffect(() => {
    if (souscriptionsRes) {
      setSouscriptionsData(souscriptionsRes);
    }
  }, [souscriptionsRes]);

  useEffect(() => {
    if (demandes) {
      setDemandesData(demandes);
    }
  }, [demandes]);

  function initialiazeButton() {
    setAjoutActif(false);
    setModifActve(true);
    setEnregActve(true);
    setSupActive(true);
    setImpActive(true);
    setActActive(true);
    setAnnulActive(true);
    setConsulActive(false);
  }

  const onSelectChange = (newSelectedRowKeys: any, selectedRows: any) => {
    setSelectedRowKeys(newSelectedRowKeys);
    const record = selectedRows?.[0] ?? souscriptionsData.find((d: any) => d.IdSouscription === newSelectedRowKeys?.[0]);
    if (record) {
      form.setFieldsValue(record);
      setSelectedSouscription(record);
    }
    setConsulActive(true);
    setAnnulActive(false);
  };
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  function consult() {
    if (selectedSouscription) {
      form.setFieldsValue(selectedSouscription);
    }
    setAnnulActive(false);
    setModifActve(false);
  }

  function handleAnulle() {
    form.resetFields();
    initialiazeButton();
    setSelectedRowKeys([]);
    setSelectedSouscription(null);
  }

  function actualiser() {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      if (souscriptionsRes) setSouscriptionsData(souscriptionsRes);
      if (demandes) setDemandesData(demandes);
      SweetAlert.fire({ title: 'Liste actualisée', icon: 'success', timer: 1000 });
    }, 500);
  }

  // Handler quand un client est sélectionné via la picklist
  function handleClientSelect(code: string, libelle: string) {
    setNumcliSelected(code ?? '');
    form.setFieldValue('NomClient', libelle ?? '');
    setSelectedRowKeys([]);
    setSelectedSouscription(null);
  }

  function demanderAssistance() {
    if (selectedSouscription) {
      if (selectedSouscription.Statut !== 'ACTIVE') {
        SweetAlert.fire({ title: 'Attention', text: 'Cette souscription n\'est pas active', icon: 'warning' });
        return;
      }
      navigate(`/assurance/demande-assistance/${selectedSouscription.IdSouscription}`);
    }
  }

  const columnsSous: ColumnsType<SouscriptionSafeCare> = [
    { title: 'Produit', dataIndex: 'NomProduit', width: 150 },
    { title: 'Version', dataIndex: 'NumeroVersion', width: 80 },
    { title: 'Compte', dataIndex: 'NumCptDebit', width: 120 },
    { title: 'Cotisation', dataIndex: 'MontantCotisation', width: 100 },
    { title: 'Date effet', dataIndex: 'DateEffet', width: 120 },
    { title: 'Date fin', dataIndex: 'DateFin', width: 120 },
    { title: 'Statut', dataIndex: 'Statut', width: 100 },
  ];

  const columnsDem: ColumnsType<DemandeSouscription> = [
    { title: 'Produit', dataIndex: 'NomProduit', width: 150 },
    { title: 'Version', dataIndex: 'NumeroVersion', width: 80 },
    { title: 'Cotisation', dataIndex: 'MontantCotisation', width: 100 },
    { title: 'Date souhaitée', dataIndex: 'DateEffetSouhaitee', width: 120 },
    { title: 'Statut', dataIndex: 'Statut', width: 100 },
    { title: 'Motif rejet', dataIndex: 'MotifRejet', ellipsis: true },
  ];

  // ==================== ONGLET FORMULAIRE SOUSCRIPTION (Tab1) ====================
  // Ce tab contient le formulaire avec picklists, comme FormTab dans ProduitAdmin.tsx
  const FormTab = () => {
    return (
      <div className="tabItem">
        <div>
          <Box fontStyle="inherit" className="border border-left" width="85%" position={embedded ? 'relative' : 'absolute'} sx={{ p: 1 }}>
            <legend>Ma souscription</legend>
            <Box display="flex" fontStyle="inherit" sx={{ mb: 1 }}>
              <Box fontStyle="inherit">
                <Form.Item name="IdSouscription" hidden>
                  <input type="hidden" />
                </Form.Item>
                <SearchModal
                  width={12}
                  Titre=""
                  label="Client (NUMCLI)"
                  name="Numcli"
                  Parametre={searchClient}
                  Taille1={167}
                  Taille2={320}
                  form={form}
                  handleFunction={handleClientSelect}
                />
                <InputForm width={9} label="Nom client" name="NomClient" type="label" />
                <InputForm width={9} label="Produit" name="NomProduit" type="label" />
                <InputForm width={9} label="Version" name="NumeroVersion" type="label" />
              </Box>
              <hr />
              <Box fontStyle="inherit">
                <InputForm width={9} label="Compte débit" name="NumCptDebit" type="label" />
                <InputForm width={9} label="Cotisation" name="MontantCotisation" type="label" />
                <InputForm width={9} label="Date effet" name="DateEffet" type="label" />
                <InputForm width={9} label="Date fin" name="DateFin" type="label" />
                <InputForm width={9} label="Statut" name="Statut" type="label" />
              </Box>
            </Box>
          </Box>
        </div>
      </div>
    );
  };

  const items: TabsProps['items'] = [
    {
      key: '1',
      label: 'Souscription',
      children: FormTab(),
    },
    {
      key: '2',
      label: 'Demandes',
      children: (
        <Box fontStyle="inherit" className="border-left tabElement" sx={{ p: 2, my: 5 }}>
          <Spin spinning={loadingDem || loading}>
            <div>
              <Table
                size="small"
                style={{ width: '100%' }}
                rowKey="IdDemande"
                columns={columnsDem}
                dataSource={demandesData}
                scroll={{ y: 250, x: 10 }}
                pagination={{ defaultPageSize: 10, position: ['bottomRight'] }}
              />
            </div>
          </Spin>
        </Box>
      ),
    },
  ];

  return (
    <div className={embedded ? undefined : 'tab safeCarePage'} style={embedded ? { position: 'relative', marginTop: 40 } : undefined}>
      {!embedded && (
        <Typography align="center" className="formTitle">
          <b>{window.location.pathname}</b>
        </Typography>
      )}
      <Spin spinning={loadingSous || loading} size="large" style={{ color: '#116599' }}>
        <Form
          disabled={!ajoutActve && !consulActive}
          autoComplete="off"
          labelAlign="left"
          form={form}
          onFinish={() => {}}
          onFinishFailed={() => {}}
          style={{ minWidth: 'auto', maxWidth: 'auto' }}
        >
          <div className="bG" style={embedded ? { top: 'auto', marginTop: 0 } : undefined}>
            <legend>
              <Typography color="#116599">Actions</Typography>
            </legend>
            <Form.Item shouldUpdate className="bouttong">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={consulActive}
                  onClick={consult}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Consulter
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={ajoutActve}
                  onClick={demanderAssistance}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Demander assistance
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={actuActive}
                  onClick={actualiser}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Actualiser
                </Button>
                <Button
                  size="large"
                  disabled={annulActive}
                  onClick={handleAnulle}
                  className="bt_add"
                  type="ghost"
                  htmlType="reset"
                  block
                >
                  Annuler
                </Button>
              </Space>
            </Form.Item>
          </div>
          <div className="clientP">
            <div>
              <Tabs
                size="small"
                type="card"
                activeKey={activeTab}
                onChange={setActiveTab}
                defaultActiveKey="1"
                items={items}
                animated={{ inkBar: true, tabPane: true }}
              />
            </div>
          </div>
        </Form>
        <br />
        <br />
        {/* Tableau en dessous du formulaire - visible seulement sur l'onglet Souscription (hors Form pour permettre le clic radio) */}
        {activeTab === '1' && (
          <Box display="flex" fontStyle="inherit" className="border-left tabElement" sx={{ p: 2, my: 5 }}>
            <Spin spinning={loadingSous || loading}>
              <div>
                <Table
                  size="small"
                  style={{ width: '100%' }}
                  rowKey="IdSouscription"
                  rowSelection={{ ...rowSelection, type: 'radio' }}
                  columns={columnsSous}
                  dataSource={souscriptionsData}
                  scroll={{ y: 250, x: 10 }}
                  pagination={{ defaultPageSize: 10, position: ['bottomRight'] }}
                />
              </div>
            </Spin>
          </Box>
        )}
      </Spin>
    </div>
  );
};

export default MesSouscriptions;